import { useState, type FormEvent } from "react";
import { api } from "../api.js";
import type { JobAvailability, JobDetail } from "../types.js";

export const availabilityLabels: Record<JobAvailability["status"], string> = {
  unknown: "待核实",
  open: "在招",
  closed: "已下架",
  expired: "已过期",
};

export function AvailabilityPanel({ job, onChanged }: { job: JobDetail; onChanged: () => Promise<void> }) {
  const current = job.availability;
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(current.status);
  const [note, setNote] = useState(current.note);
  const [sources, setSources] = useState(current.evidence_urls.join("\n") || job.source_url);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const needsEvidence = status === "closed" || status === "expired";

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api(`/api/v1/jobs/${job.id}/availability`, {
        method: "PUT",
        body: JSON.stringify({
          status,
          checked_at: new Date().toISOString(),
          note,
          evidence_urls: sources
            .split("\n")
            .map((url) => url.trim())
            .filter(Boolean),
        }),
      });
      await onChanged();
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "招聘状态保存失败，请重试。");
    } finally {
      setSaving(false);
    }
  }

  function beginEdit() {
    setStatus(current.status);
    setNote(current.note);
    setSources(current.evidence_urls.join("\n") || job.source_url);
    setError("");
    setEditing(true);
  }

  return (
    <section className="availability-panel" aria-label="招聘状态">
      <div className="availability-heading">
        <span>招聘状态</span>
        <span className={`availability-badge availability-${current.status}`}>
          {availabilityLabels[current.status]}
        </span>
        {!editing && (
          <button type="button" className="availability-edit" onClick={beginEdit}>
            修改
          </button>
        )}
      </div>
      {current.checked_at ? (
        <small>
          {new Date(current.checked_at).toLocaleString("zh-CN")} 核查 · {current.actor_name}
        </small>
      ) : (
        <small>尚未核查招聘页面</small>
      )}
      {current.note && <p>{current.note}</p>}
      {!!current.evidence_urls.length && (
        <div className="availability-sources">
          {current.evidence_urls.map((url, index) => (
            <a key={`${index}-${url}`} href={url} target="_blank" rel="noreferrer">
              核查来源 {index + 1}
            </a>
          ))}
        </div>
      )}
      {editing && (
        <form className="availability-form" onSubmit={save}>
          <label>
            招聘状态
            <select
              aria-label="修改招聘状态"
              value={status}
              disabled={saving}
              onChange={(event) => setStatus(event.target.value as JobAvailability["status"])}
            >
              {Object.entries(availabilityLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <small>已下架：撤下或招满。已过期：明确超过申请截止日期。</small>
          <label>
            核查说明
            <textarea
              aria-label="核查说明"
              rows={2}
              maxLength={5000}
              required={needsEvidence}
              disabled={saving}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="说明官方页面或招聘系统的核查结果"
            />
          </label>
          <label>
            来源链接（每行一条）
            <textarea
              aria-label="核查来源链接"
              rows={2}
              required={needsEvidence}
              disabled={saving}
              value={sources}
              onChange={(event) => setSources(event.target.value)}
            />
          </label>
          {error && (
            <p role="alert" className="availability-error">
              {error}
            </p>
          )}
          <div className="availability-buttons">
            <button className="button primary" type="submit" disabled={saving}>
              {saving ? "保存中…" : "保存状态"}
            </button>
            <button
              className="button secondary"
              type="button"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              取消
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
